import { useAuth } from "@/providers";
import {
  useSearchTablePreferences,
  useUpdateSearchTablePreferences,
} from "@/shared/domain/config/hooks";

import {
  DEFAULT_ROWS_PER_PAGE,
  DEFAULT_TABLE_COLUMNS,
  TableType,
} from "./tablePreferencesConfig";
import {
  useCreatePreferenceCallbacks,
  createPreferencesWithDefaults,
  useOrgPreferences,
} from "./tablePreferencesHelpers";

/**
 * Hook to manage device table preferences (columns and rows per page).
 * Provides the current preferences and functions to update them.
 * Preferences are persisted to the backend via the config system.
 * Preferences are org-specific and fall back to defaults if not set for the current org.
 *
 * @returns Object containing preferences, save functions, loading states, and error handling
 */
export function useDeviceTablePreferences() {
  const { currentOrganization } = useAuth();

  const configQuery = useSearchTablePreferences(
    "device_search_table_preferences",
  );
  const updateMutation = useUpdateSearchTablePreferences(
    "device_search_table_preferences",
  );

  const orgPreferences = useOrgPreferences(
    configQuery.data,
    currentOrganization?.org_id,
  );

  const preferences = createPreferencesWithDefaults(
    orgPreferences,
    DEFAULT_TABLE_COLUMNS[TableType.Devices],
    DEFAULT_ROWS_PER_PAGE,
  );

  const { saveColumns, saveRowsPerPage, savePreferences, clearError } =
    useCreatePreferenceCallbacks(updateMutation, currentOrganization?.org_id);

  return {
    preferences,
    saveColumns,
    saveRowsPerPage,
    savePreferences,
    isLoading: configQuery.isLoading,
    isSaving: updateMutation.isPending,
    error: updateMutation.error,
    clearError,
  };
}
